import React from 'react';
import { Helmet } from 'react-helmet-async';

const SEOSchema = () => {
  const domain = window.location.origin;

  const schema = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "WebPage",
        "@id": `${domain}/#webpage`,
        "url": domain,
        "name": "Golf Like a Caddie: Volume 1 - Free Guide | Ryan Tewksbury",
        "description": "Get the professional caddie secrets that tour pros pay $2,000/week for. Learn to score better without changing your swing. Free guide by Ryan Tewksbury.",
        "inLanguage": "en-US",
        "isPartOf": {
          "@id": `${domain}/#website`
        },
        "primaryImageOfPage": {
          "@id": `${domain}/#primaryimage`
        },
        "datePublished": "2025-01-26",
        "dateModified": "2025-01-26"
      },
      {
        "@type": "WebSite",
        "@id": `${domain}/#website`,
        "url": domain,
        "name": "Golf Like a Caddie",
        "publisher": {
          "@id": `${domain}/#author`
        }
      },
      {
        "@type": ["Book", "DigitalDocument"],
        "@id": `${domain}/#book`,
        "name": "Golf Like a Caddie: Volume 1",
        "alternateName": "The Complete Caddie's Guide to Course Management",
        "description": "A comprehensive guide featuring 10+ years of professional caddie secrets, including course management strategies, mental game techniques, and proven methods to lower your golf scores without changing your swing.",
        "bookFormat": "EBook",
        "numberOfPages": "27",
        "inLanguage": "en-US",
        "author": {
          "@id": `${domain}/#author`
        },
        "about": [
          "Golf strategy",
          "Course management",
          "Golf mental game",
          "Caddie techniques",
          "Golf scoring"
        ],
        "keywords": "golf caddie guide, course management, golf strategy, lower golf scores, golf mental game, professional caddie secrets",
        "offers": {
          "@type": "Offer",
          "price": "0",
          "priceCurrency": "USD",
          "availability": "https://schema.org/InStock",
          "availabilityStarts": "2025-01-26",
          "seller": {
            "@id": `${domain}/#author`
          }
        },
        "image": {
          "@type": "ImageObject",
          "url": "https://res.cloudinary.com/dzcrxivpm/image/upload/v1758745884/glac_mockup_yqpv6v.webp",
          "width": 800,
          "height": 1200
        },
        "hasPart": [
          {
            "@type": "Chapter",
            "name": "Chapter 1: How to Hit More Greens",
            "description": "Learn the 3 critical numbers every caddie knows before pulling a club"
          },
          {
            "@type": "Chapter",
            "name": "Chapter 2: Aim Closer",
            "description": "The intermediate target method that tour pros use for perfect alignment"
          },
          {
            "@type": "Chapter",
            "name": "Chapter 3: Routine",
            "description": "Build an unshakeable pre-shot routine that eliminates doubt"
          },
          {
            "@type": "Chapter",
            "name": "Chapter 4: Know Your Numbers",
            "description": "Discover your true stock yardages and stop guessing distances"
          },
          {
            "@type": "Chapter",
            "name": "Chapter 5: Read Every Putt",
            "description": "The Compass Method for reading putts from 4 angles"
          },
          {
            "@type": "Chapter",
            "name": "Chapter 6: Chip More, Pitch Less",
            "description": "The 80/20 short game rule that tour pros follow"
          }
        ]
      },
      {
        "@type": "Person",
        "@id": `${domain}/#author`,
        "name": "Ryan Tewksbury",
        "jobTitle": "Professional Golf Caddie",
        "description": "Professional caddie with 10+ years of experience working with players at every skill level, specializing in course management and golf strategy.",
        "image": {
          "@type": "ImageObject",
          "url": "https://res.cloudinary.com/dzcrxivpm/image/upload/v1758771712/ryanheadshot_rwnmbn.webp"
        },
        "knowsAbout": [
          "Golf course management",
          "Golf strategy",
          "Caddie techniques",
          "Golf mental game"
        ]
      },
      {
        "@type": "ImageObject",
        "@id": `${domain}/#primaryimage`,
        "url": "https://res.cloudinary.com/dzcrxivpm/image/upload/v1758745884/glac_mockup_yqpv6v.webp",
        "width": 800,
        "height": 1200,
        "caption": "Golf Like a Caddie Volume 1 E-book Cover"
      },
      {
        "@type": "FAQPage",
        "mainEntity": [
          {
            "@type": "Question",
            "name": "What is Golf Like a Caddie Volume 1?",
            "acceptedAnswer": {
              "@type": "Answer",
              "text": "Golf Like a Caddie Volume 1 is a 27-page guide featuring professional caddie secrets and course management strategies to help golfers score better without changing their swing."
            }
          },
          {
            "@type": "Question",
            "name": "How long is the ebook?",
            "acceptedAnswer": {
              "@type": "Answer",
              "text": "The ebook is 27 pages long, featuring 6 comprehensive chapters on golf strategy and course management."
            }
          },
          {
            "@type": "Question",
            "name": "Is this ebook really free?",
            "acceptedAnswer": {
              "@type": "Answer",
              "text": "Yes, Golf Like a Caddie Volume 1 is completely free with no hidden costs or upsells. Simply enter your email to receive instant access."
            }
          },
          {
            "@type": "Question",
            "name": "Who is Ryan Tewksbury?",
            "acceptedAnswer": {
              "@type": "Answer",
              "text": "Ryan Tewksbury is a professional golf caddie with over 10 years of experience working with players at every skill level at various private golf courses."
            }
          },
          {
            "@type": "Question",
            "name": "Do I need to change my swing to benefit from this guide?",
            "acceptedAnswer": {
              "@type": "Answer",
              "text": "No swing changes are required. This guide focuses on course management, strategy, and mental game improvements that work with your current golf game."
            }
          }
        ]
      },
      {
        "@type": "HowTo",
        "name": "How to Improve Your Golf Score Using Caddie Strategies",
        "description": "Learn professional caddie techniques to lower your golf scores through better course management and strategic decision-making.",
        "totalTime": "PT30M",
        "step": [
          {
            "@type": "HowToStep",
            "position": 1,
            "name": "Master Green Targeting",
            "text": "Learn the 3 critical numbers every caddie knows: distance to front, back, and pin position. Use strategic targeting instead of always aiming at the flag."
          },
          {
            "@type": "HowToStep",
            "position": 2,
            "name": "Perfect Your Alignment",
            "text": "Use the intermediate target method by picking a target 2-3 feet in front of your ball to ensure proper alignment on every shot."
          },
          {
            "@type": "HowToStep",
            "position": 3,
            "name": "Build Your Pre-Shot Routine",
            "text": "Develop a consistent 5-step pre-shot routine that eliminates doubt and performs under pressure."
          },
          {
            "@type": "HowToStep",
            "position": 4,
            "name": "Know Your True Distances",
            "text": "Calculate your real carry distances for each club and stop using 'best case' numbers that lead to under-clubbing."
          },
          {
            "@type": "HowToStep",
            "position": 5,
            "name": "Read Putts Like a Pro",
            "text": "Use the Compass Method to read putts from 4 different angles and identify subtle breaks that most golfers miss."
          },
          {
            "@type": "HowToStep",
            "position": 6,
            "name": "Simplify Your Short Game",
            "text": "Follow the 80/20 rule: chip more and pitch less. Learn when to use each shot type for better consistency around the greens."
          }
        ]
      },
      {
        "@type": "BreadcrumbList",
        "itemListElement": [
          {
            "@type": "ListItem",
            "position": 1,
            "name": "Home",
            "item": domain
          }
        ]
      }
    ]
  };

  return (
    <Helmet>
      <script type="application/ld+json">
        {JSON.stringify(schema)}
      </script>
    </Helmet>
  );
};

export default SEOSchema;
